/**
 * Search interface component
 */

import { DomUtils } from '../../utils/DomUtils.js';
import { KeyboardUtils } from '../../utils/KeyboardUtils.js';
import { DebounceUtils } from '../../utils/DebounceUtils.js';
import { MESSAGES } from '../../constants/Messages.js';
import { SUGGESTIONS, SEARCH_CONFIG, COOKIE_NAMES } from '../../constants/Defaults.js';
import { ApiService } from '../../services/ApiService.js';
import { CookieService } from '../../utils/CookieService.js';

export class SearchInterface {
  constructor(options = {}, eventBus) {
    this.options = options;
    this.eventBus = eventBus;
    this.isVisible = false;
    this.searchInput = null;
    this.overlayElement = null;
    this.searchElement = null;
    this.suggestionsElement = null;
    this.resultsElement = null;
    this.loadingElement = null;
    this.badgeElement = null;
    this.errorElement = null;
    this.searchTimeout = null;
    this.selectedResultIndex = -1;
    this.currentResults = [];
    this.selectedSuggestionIndex = -1;
    
    // Cookie names for flow points
    this.flowPointsCookieName = COOKIE_NAMES.flowPoints;
    this.flowCurrentIdxCookieName = COOKIE_NAMES.flowCurrentIdx;
    
    // Initialize API service
    this.apiService = new ApiService(options);
  }

  /**
   * Create the search interface
   */
  create() {
    this.createOverlay();
    this.createSearchContainer();
    this.createSearchInput();
    this.createAISuggestionBlock();
    this.createSuggestions();
    this.createBadge();
    this.bindEvents();
    this.updateSuggestions();
  }

  /**
   * Create the overlay
   */
  createOverlay() {
    this.overlayElement = DomUtils.createElement('div', 'flowlight-overlay', {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: this.options.zIndex - 1,
      display: 'none',
      opacity: '0',
      transition: 'opacity 0.3s ease'
    });

    document.body.appendChild(this.overlayElement);
  }

  /**
   * Create the search container
   */
  createSearchContainer() {
    this.searchElement = DomUtils.createElement('div', 'flowlight-search', {
      position: 'fixed',
      top: '25%',
      left: '50%',
      transform: 'translate(-50%, -50%) scale(0.8)',
      width: '800px',
      maxWidth: '90vw',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
      zIndex: this.options.zIndex,
      display: 'none',
      opacity: '0',
      transition: 'all 0.3s ease',
      overflow: 'hidden',
      border: '1px solid #f0f0f0'
    });

    document.body.appendChild(this.searchElement);
  }

  /**
   * Create the search input
   */
  createSearchInput() {
    // Create a container for the search input and shortcut indicator
    const searchInputContainer = DomUtils.createElement('div', 'flowlight-search-input-container', {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      width: '100%'
    });

    this.searchInput = DomUtils.createElement('input', 'flowlight-search-input', {
      flex: '1',
      padding: '20px 24px',
      paddingRight: '80px', // Make room for the shortcut indicator
      border: 'none',
      outline: 'none',
      fontSize: '16px',
      backgroundColor: 'transparent',
      color: '#333',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontWeight: '400'
    });

    this.searchInput.type = 'text';
    this.updateSearchPlaceholder();

    // Create the keyboard shortcut indicator
    const shortcutIndicator = DomUtils.createElement('div', 'flowlight-shortcut-indicator', {
      position: 'absolute',
      right: '16px',
      top: '50%',
      transform: 'translateY(-50%)',
      padding: '4px 8px',
      backgroundColor: '#f3f4f6',
      borderRadius: '6px',
      fontSize: '12px',
      color: '#6b7280',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontWeight: '500',
      border: '1px solid #e5e7eb',
      pointerEvents: 'none',
      userSelect: 'none'
    });

    // Update shortcut text based on platform
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    shortcutIndicator.textContent = isMac ? '⌘K' : 'Ctrl+K';

    searchInputContainer.appendChild(this.searchInput);
    searchInputContainer.appendChild(shortcutIndicator);
    this.searchElement.appendChild(searchInputContainer);
  }

  /**
   * Update search placeholder based on input width
   */
  updateSearchPlaceholder() {
    // Use shorter placeholder for smaller inputs
    const useShortPlaceholder = this.searchInput && this.searchInput.offsetWidth < 400;
    this.searchInput.placeholder = useShortPlaceholder 
      ? MESSAGES.searchPlaceholderShort 
      : MESSAGES.searchPlaceholder;
  }

  /**
   * Update shortcut indicator based on platform
   */
  updateShortcutIndicator() {
    const shortcutIndicator = this.searchElement?.querySelector('.flowlight-shortcut-indicator');
    if (shortcutIndicator) {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      shortcutIndicator.textContent = isMac ? '⌘K' : 'Ctrl+K';
    }
  }

  /**
   * Manually update search placeholder (for testing)
   * @param {string} placeholder - Custom placeholder text
   */
  setSearchPlaceholder(placeholder) {
    if (this.searchInput) {
      this.searchInput.placeholder = placeholder;
    }
  }

  /**
   * Create suggestions container
   */
  createSuggestions() {
    this.suggestionsElement = DomUtils.createElement('div', 'flowlight-suggestions', {
      padding: '16px 24px',
      backgroundColor: '#f8f9fa',
      borderTop: '1px solid #e9ecef',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      maxHeight: '120px',
      overflowY: 'auto'
    });

    this.searchElement.appendChild(this.suggestionsElement);
  }

  /**
   * Create AI suggestion block
   */
  createAISuggestionBlock() {
    const aiBlock = DomUtils.createElement('div', 'flowlight-ai-suggestion', {
      margin: '0 24px 16px 24px',
      padding: '16px 20px',
      backgroundColor: '#f3f4f6',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      border: '1px solid #e5e7eb'
    });

    // Left side with icon and text
    const leftSide = DomUtils.createElement('div', 'flowlight-ai-left', {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    });

    // Purple sparkle icon
    const sparkleIcon = DomUtils.createElement('div', 'flowlight-sparkle-icon', {
      width: '20px',
      height: '20px',
      backgroundColor: '#8b5cf6',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '12px',
      fontWeight: 'bold'
    });
    sparkleIcon.innerHTML = '✨';

    // Text content
    const textContent = DomUtils.createElement('div', 'flowlight-ai-text', {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px'
    });

    const mainText = DomUtils.createElement('div', 'flowlight-ai-main-text', {
      color: '#6b21a8',
      fontSize: '14px',
      fontWeight: '600',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    });

    const subText = DomUtils.createElement('div', 'flowlight-ai-sub-text', {
      color: '#6b7280',
      fontSize: '12px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    });

    textContent.appendChild(mainText);
    textContent.appendChild(subText);
    leftSide.appendChild(sparkleIcon);
    leftSide.appendChild(textContent);

    // Right side with arrow icon
    const rightSide = DomUtils.createElement('div', 'flowlight-ai-right', {
      width: '16px',
      height: '16px',
      backgroundColor: '#8b5cf6',
      borderRadius: '3px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '10px'
    });
    rightSide.innerHTML = '↗';

    aiBlock.appendChild(leftSide);
    aiBlock.appendChild(rightSide);

    // Store references for updating
    this.aiMainText = mainText;
    this.aiSubText = subText;

    this.searchElement.appendChild(aiBlock);
    this.aiSuggestionBlock = aiBlock;
  }

  /**
   * Create powered by badge
   */
  createBadge() {
    this.badgeElement = DomUtils.createElement('div', 'flowlight-powered-by', {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 24px',
      backgroundColor: 'white',
      borderTop: '1px solid #e5e7eb',
      fontSize: '12px',
      color: '#6c757d',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    });

    // Left side - Powered by
    const leftSide = DomUtils.createElement('div', 'flowlight-powered-left', {
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    });

    leftSide.innerHTML = `
      <span style="color: #9ca3af;">${MESSAGES.poweredBy}</span>
      <span style="color: #374151; font-weight: 600;">▶</span>
      <span style="color: #111827; font-weight: 600;">${MESSAGES.poweredByBrand}</span>
    `;

    // Right side - Navigation controls
    const rightSide = DomUtils.createElement('div', 'flowlight-navigation', {
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    });

    // Navigate section
    const navigateSection = DomUtils.createElement('div', 'flowlight-navigate', {
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    });

    navigateSection.innerHTML = `
      <span style="color: #9ca3af; font-size: 10px;">↑</span>
      <span style="color: #9ca3af; font-size: 10px;">↓</span>
      <span style="color: #9ca3af;">Navigate</span>
    `;

    // Select section
    const selectSection = DomUtils.createElement('div', 'flowlight-select', {
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    });

    selectSection.innerHTML = `
      <span style="color: #9ca3af; font-size: 10px;">↵</span>
      <span style="color: #9ca3af;">Select</span>
    `;

    rightSide.appendChild(navigateSection);
    rightSide.appendChild(selectSection);

    this.badgeElement.appendChild(leftSide);
    this.badgeElement.appendChild(rightSide);

    this.searchElement.appendChild(this.badgeElement);
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Overlay click to hide
    this.overlayElement.addEventListener('click', (e) => {
      // Only hide if clicking directly on the overlay, not on child elements
      if (e.target === this.overlayElement) {
        this.hide();
      }
    });

    // Window resize to update placeholder and shortcut indicator
    this.handleResize = () => {
      if (this.isVisible) {
        this.updateSearchPlaceholder();
        this.updateShortcutIndicator();
      }
    };
    window.addEventListener('resize', this.handleResize);

    // Search input events
    this.searchInput.addEventListener('keydown', (e) => {
      if (KeyboardUtils.isEscape(e)) {
        this.hide();
      } else if (KeyboardUtils.isArrowDown(e)) {
        e.preventDefault();
        // If suggestions are visible, navigate suggestions first
        if (this.suggestionsElement && this.suggestionsElement.style.display !== 'none') {
          this.navigateSuggestions('down');
        } else {
          this.navigateResults('down');
        }
      } else if (KeyboardUtils.isArrowUp(e)) {
        e.preventDefault();
        // If suggestions are visible, navigate suggestions first
        if (this.suggestionsElement && this.suggestionsElement.style.display !== 'none') {
          this.navigateSuggestions('up');
        } else {
          this.navigateResults('up');
        }
      } else if (KeyboardUtils.isEnter(e)) {
        e.preventDefault();
        // If a suggestion is selected, select it
        if (this.selectedSuggestionIndex >= 0) {
          this.selectCurrentSuggestion();
        } else {
          this.selectCurrentResult().catch(error => {
            console.error('Error selecting current result:', error);
          });
        }
      } else if (KeyboardUtils.isTab(e)) {
        e.preventDefault();
        this.selectFirstSuggestion();
      }
    });

    // Input event for debounced search
    this.searchInput.addEventListener('input', (e) => {
      const query = e.target.value.trim();
      
      // Update suggestions immediately
      this.updateSuggestions(query);
      
      // Clear existing timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      // Debounce the search
      if (query.length >= SEARCH_CONFIG.minQueryLength) {
        this.searchTimeout = setTimeout(() => {
          this.handleSearch(query);
        }, this.options.searchDebounceDelay || 500);
      } else if (query.length === 0) {
        this.clearSearchResults();
      }
    });
  }

  /**
   * Show the search interface
   */
  show() {
    this.isVisible = true;
    
    // Show overlay
    this.overlayElement.style.display = 'block';
    setTimeout(() => {
      this.overlayElement.style.opacity = '1';
    }, 10);
    
    // Show search
    this.searchElement.style.display = 'block';
    setTimeout(() => {
      this.searchElement.style.opacity = '1';
      this.searchElement.style.transform = 'translate(-50%, -50%) scale(1)';
    }, 10);
    
    // Focus input and update placeholder
    setTimeout(() => {
      this.searchInput.focus();
      this.updateSearchPlaceholder();
      this.updateShortcutIndicator();
    }, 100);
    
    if (this.options.debug) {
      console.log(MESSAGES.searchShown);
    }
  }

  /**
   * Hide the search interface
   */
  hide() {
    this.isVisible = false;
    
    // Hide search
    this.searchElement.style.opacity = '0';
    this.searchElement.style.transform = 'translate(-50%, -50%) scale(0.8)';
    
    // Hide overlay
    this.overlayElement.style.opacity = '0';
    
    setTimeout(() => {
      this.searchElement.style.display = 'none';
      this.overlayElement.style.display = 'none';
      this.searchInput.value = '';
      
      // Clear any pending search timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }
      
      // Clean up results and loading
      if (this.resultsElement) {
        this.resultsElement.remove();
        this.resultsElement = null;
      }
      if (this.loadingElement) {
        this.loadingElement.style.display = 'none';
      }
      
      // Reset selection state
      this.selectedResultIndex = -1;
      this.currentResults = [];
      
      this.updateSuggestions(); // Reset suggestions
    }, 300);
    
    if (this.options.debug) {
      console.log(MESSAGES.searchHidden);
    }
  }

  /**
   * Toggle search visibility
   */
  toggle() {
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }

  /**
   * Update suggestions based on search input
   */
  updateSuggestions(query = '') {
    if (!this.suggestionsElement) return;
    
    console.log('📝 Updating suggestions:', {
      query: query,
      timestamp: new Date().toISOString()
    });
    
    // Update AI suggestion block
    if (this.aiMainText && this.aiSubText) {
      if (query) {
        this.aiMainText.textContent = `Ask "${query}"`;
        this.aiSubText.textContent = 'Get an answer with AI';
        this.aiSuggestionBlock.style.display = 'flex';
      } else {
        this.aiSuggestionBlock.style.display = 'none';
      }
    }
    
    // Clear existing suggestions
    this.suggestionsElement.innerHTML = '';
    this.selectedSuggestionIndex = -1;
    
    // Filter suggestions based on query
    const filteredSuggestions = SUGGESTIONS.filter(suggestion =>
      suggestion.toLowerCase().includes(query.toLowerCase())
    );
    
    // Show all suggestions if no query, or filtered suggestions
    const suggestionsToShow = query ? filteredSuggestions : SUGGESTIONS;
    
    // Limit to 6 suggestions
    const limitedSuggestions = suggestionsToShow.slice(0, 6);
    
    limitedSuggestions.forEach((suggestion, index) => {
      const suggestionElement = DomUtils.createElement('button', 'flowlight-suggestion', {
        padding: '6px 12px',
        backgroundColor: 'white',
        border: '1px solid #dee2e6',
        borderRadius: '16px',
        fontSize: '12px',
        color: '#495057',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        outline: 'none'
      }, suggestion);
      
      // Store suggestion data
      suggestionElement.dataset.suggestion = suggestion;
      suggestionElement.dataset.index = index;
      
      // Add hover effects
      suggestionElement.addEventListener('mouseenter', () => {
        console.log('🖱️ Suggestion hover enter:', {
          suggestion: suggestion,
          index: index,
          timestamp: new Date().toISOString()
        });
        
        this.selectedSuggestionIndex = index;
        this.updateSuggestionSelection();
      });
      
      suggestionElement.addEventListener('mouseleave', () => {
        console.log('🖱️ Suggestion hover leave:', {
          suggestion: suggestion,
          index: index,
          timestamp: new Date().toISOString()
        });
        
        this.selectedSuggestionIndex = -1;
        this.updateSuggestionSelection();
      });
      
      // Add click handler
      suggestionElement.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        console.log('🎯 Suggestion clicked!', {
          suggestion: suggestion,
          index: index,
          element: suggestionElement,
          eventTarget: e.target,
          timestamp: new Date().toISOString()
        });
        
        if (this.options.debug) {
          console.log('Suggestion clicked:', suggestion);
          console.log('Suggestion element:', suggestionElement);
          console.log('Event target:', e.target);
        }
        
        this.selectSuggestion(suggestion);
      });
      
      // Also add a simple test click to verify the element is clickable
      suggestionElement.addEventListener('mousedown', (e) => {
        console.log('👆 Suggestion mousedown:', {
          suggestion: suggestion,
          index: index,
          timestamp: new Date().toISOString()
        });
        
        if (this.options.debug) {
          console.log('Suggestion mousedown:', suggestion);
        }
      });
      
      this.suggestionsElement.appendChild(suggestionElement);
      
      console.log('➕ Suggestion element created:', {
        suggestion: suggestion,
        index: index,
        element: suggestionElement,
        timestamp: new Date().toISOString()
      });
    });
    
    console.log('✅ Suggestions update completed:', {
      totalSuggestions: limitedSuggestions.length,
      query: query,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Handle search input
   */
  async handleSearch(query) {
    if (this.options.debug) {
      console.log('Search query:', query);
    }
    
    // Check minimum query length
    if (query.length < SEARCH_CONFIG.minQueryLength) {
      this.hideLoadingState();
      this.showSearchResults([]);
      return;
    }
    
    // Show loading state
    this.showLoadingState();
    
    // Emit search event for other components to handle
    this.eventBus.emit('search:query', { query });
    
    try {
      // Call the API service
      const searchResults = await this.apiService.searchFlows(query, {
        limit: SEARCH_CONFIG.limit
      });
      
      if (this.options.debug) {
        console.log('Search results:', searchResults);
      }
      
      // Hide loading and show results
      this.hideLoadingState();
      this.showSearchResults(searchResults.flows || searchResults || []);
      
    } catch (error) {
      if (this.options.debug) {
        console.error('Search API error:', error);
      }
      
      // Hide loading and show error
      this.hideLoadingState();
      this.showSearchError(error.message);
    }
  }

  /**
   * Show loading state
   */
  showLoadingState() {
    // Clear any existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
    
    // Hide suggestions and AI block
    if (this.suggestionsElement) {
      this.suggestionsElement.style.display = 'none';
    }
    if (this.aiSuggestionBlock) {
      this.aiSuggestionBlock.style.display = 'none';
    }
    
    // Create loading element if it doesn't exist
    if (!this.loadingElement) {
      this.loadingElement = DomUtils.createElement('div', 'flowlight-loading', {
        padding: '20px 24px',
        backgroundColor: 'white',
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px'
      });
      
      this.loadingElement.innerHTML = `
        <div class="flowlight-waves">
          <div class="wave"></div>
          <div class="wave"></div>
          <div class="wave"></div>
        </div>
        <span style="color: #6b7280; font-size: 14px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">${MESSAGES.thinking}</span>
      `;
      
      // Add wave animation styles
      const waveStyles = DomUtils.addStyles(`
        .flowlight-waves {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .flowlight-waves .wave {
          width: 4px;
          height: 20px;
          background: #8b5cf6;
          border-radius: 2px;
          animation: flowlight-wave 1.2s ease-in-out infinite;
        }
        .flowlight-waves .wave:nth-child(2) {
          animation-delay: 0.2s;
        }
        .flowlight-waves .wave:nth-child(3) {
          animation-delay: 0.4s;
        }
        @keyframes flowlight-wave {
          0%, 40%, 100% {
            transform: scaleY(0.4);
          }
          20% {
            transform: scaleY(1);
          }
        }
      `, 'flowlight-wave-styles');
      
      // Insert loading before the badge
      this.searchElement.insertBefore(this.loadingElement, this.badgeElement);
    }
    
    this.loadingElement.style.display = 'flex';
  }

  /**
   * Hide loading state
   */
  hideLoadingState() {
    if (this.loadingElement) {
      this.loadingElement.style.display = 'none';
    }
    
    // Show AI suggestion block again if no results are being shown
    if (this.aiSuggestionBlock && !this.resultsElement) {
      this.aiSuggestionBlock.style.display = 'flex';
    }
  }

  /**
   * Show error state
   */
  showErrorState(errorMessage) {
    // Hide loading state first
    this.hideLoadingState();
    
    // Create error element if it doesn't exist
    if (!this.errorElement) {
      this.errorElement = DomUtils.createElement('div', 'flowlight-error', {
        padding: '16px 24px',
        backgroundColor: '#f8d7da',
        border: '1px solid #f5c6cb',
        borderRadius: '8px',
        color: '#721c24',
        fontSize: '14px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        marginTop: '8px'
      });
      
      this.searchElement.appendChild(this.errorElement);
    }
    
    // Show error message
    this.errorElement.textContent = errorMessage;
    this.errorElement.style.display = 'block';
    
    // Auto-hide error after 3 seconds
    setTimeout(() => {
      if (this.errorElement) {
        this.errorElement.style.display = 'none';
      }
    }, 3000);
  }

  /**
   * Show search error
   */
  showSearchError(errorMessage) {
    // Remove existing results
    if (this.resultsElement) {
      this.resultsElement.remove();
    }
    
    this.resultsElement = DomUtils.createElement('div', 'flowlight-results', {
      padding: '16px 24px',
      backgroundColor: '#f8f9fa',
      borderTop: '1px solid #e9ecef',
      maxHeight: '300px',
      overflowY: 'auto'
    });
    
    this.resultsElement.innerHTML = `
      <div style="text-align: center; color: #dc3545; font-size: 14px; padding: 20px;">
        <div style="margin-bottom: 8px; font-weight: 600;">${MESSAGES.searchError || 'Search Error'}</div>
        <div style="font-size: 12px; color: #6c757d;">${errorMessage}</div>
      </div>
    `;
    
    // Insert results before the badge
    this.searchElement.insertBefore(this.resultsElement, this.badgeElement);
  }

  /**
   * Show search results
   */
  showSearchResults(flows) {
    // Remove existing results
    if (this.resultsElement) {
      this.resultsElement.remove();
    }
    
    // Hide AI suggestion block when showing results
    if (this.aiSuggestionBlock) {
      this.aiSuggestionBlock.style.display = 'none';
    }
    
    // Store current results and reset selection
    this.currentResults = flows;
    this.selectedResultIndex = -1;
    
    this.resultsElement = DomUtils.createElement('div', 'flowlight-results', {
      padding: '16px 24px',
      backgroundColor: 'white',
      borderTop: '1px solid #e5e7eb',
      maxHeight: '300px',
      overflowY: 'auto'
    });
    
    if (flows.length === 0) {
      this.resultsElement.innerHTML = `
        <div style="text-align: center; color: #9ca3af; font-size: 14px; padding: 20px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
          No results found 😉
        </div>
      `;
    } else {
      flows.forEach((flow, index) => {
        const flowElement = DomUtils.createElement('div', 'flowlight-flow-item', {
          padding: '12px 16px',
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          marginBottom: '8px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        });
        
        // Add hover effects
        flowElement.addEventListener('mouseenter', () => {
          flowElement.style.backgroundColor = '#f9fafb';
          flowElement.style.borderColor = '#d1d5db';
        });
        
        flowElement.addEventListener('mouseleave', () => {
          flowElement.style.backgroundColor = 'white';
          flowElement.style.borderColor = '#e5e7eb';
        });
        
        // Add click handler
        flowElement.addEventListener('click', async () => {
          this.selectedResultIndex = index;
          await this.selectFlow(flow);
        });
        
        // Display flow information
        flowElement.innerHTML = `
          <div style="font-weight: 600; color: #111827; margin-bottom: 4px;">
            ${flow.name || flow.title || `Flow ${index + 1}`}
          </div>
          <div style="font-size: 12px; color: #6b7280;">
            ${flow.description || MESSAGES.noDescription}
          </div>
        `;
        
        this.resultsElement.appendChild(flowElement);
      });
    }
    
    // Insert results before the badge
    this.searchElement.insertBefore(this.resultsElement, this.badgeElement);
  }

  /**
   * Clear search results
   */
  clearSearchResults() {
    if (this.resultsElement) {
      this.resultsElement.remove();
      this.resultsElement = null;
    }
    if (this.loadingElement) {
      this.loadingElement.style.display = 'none';
    }
    this.selectedResultIndex = -1;
    this.currentResults = [];
    
    // Show AI suggestion block again
    if (this.aiSuggestionBlock) {
      this.aiSuggestionBlock.style.display = 'flex';
    }
    
    this.showSuggestions();
  }

  /**
   * Show suggestions (helper method)
   */
  showSuggestions() {
    if (this.suggestionsElement) {
      this.suggestionsElement.style.display = 'flex';
    }
    if (this.aiSuggestionBlock) {
      this.aiSuggestionBlock.style.display = 'flex';
    }
  }

  /**
   * Select a suggestion
   */
  selectSuggestion(suggestion) {
    console.log('🚀 Selecting suggestion:', {
      suggestion: suggestion,
      currentInputValue: this.searchInput.value,
      isVisible: this.isVisible,
      timestamp: new Date().toISOString()
    });
    
    if (this.options.debug) {
      console.log('selectSuggestion called with:', suggestion);
    }
    
    this.searchInput.value = suggestion;
    this.searchInput.focus();
    this.handleSearch(suggestion);
    
    // Emit suggestion selected event
    this.eventBus.emit('suggestion:selected', { suggestion });
    
    console.log('✅ Suggestion selection completed:', {
      suggestion: suggestion,
      newInputValue: this.searchInput.value,
      timestamp: new Date().toISOString()
    });
    
    if (this.options.debug) {
      console.log('Suggestion selected:', suggestion);
    }
  }

  /**
   * Select the first suggestion
   */
  selectFirstSuggestion() {
    const firstSuggestion = this.suggestionsElement?.querySelector('.flowlight-suggestion');
    if (firstSuggestion) {
      const suggestion = firstSuggestion.dataset.suggestion;
      this.selectSuggestion(suggestion);
    }
  }

  /**
   * Update suggestion selection visual state
   */
  updateSuggestionSelection() {
    const suggestions = this.suggestionsElement?.querySelectorAll('.flowlight-suggestion');
    if (!suggestions) return;
    
    suggestions.forEach((suggestion, index) => {
      if (index === this.selectedSuggestionIndex) {
        // Selected state
        suggestion.style.backgroundColor = '#e3f2fd';
        suggestion.style.borderColor = '#2196f3';
        suggestion.style.color = '#1976d2';
        suggestion.style.boxShadow = '0 2px 8px rgba(33, 150, 243, 0.3)';
      } else {
        // Default state
        suggestion.style.backgroundColor = 'white';
        suggestion.style.borderColor = '#dee2e6';
        suggestion.style.color = '#495057';
        suggestion.style.boxShadow = 'none';
      }
    });
  }

  /**
   * Navigate through suggestions with arrow keys
   */
  navigateSuggestions(direction) {
    const suggestions = this.suggestionsElement?.querySelectorAll('.flowlight-suggestion');
    if (!suggestions || suggestions.length === 0) return;
    
    if (direction === 'down') {
      this.selectedSuggestionIndex = Math.min(this.selectedSuggestionIndex + 1, suggestions.length - 1);
    } else if (direction === 'up') {
      this.selectedSuggestionIndex = Math.max(this.selectedSuggestionIndex - 1, -1);
    }
    
    this.updateSuggestionSelection();
  }

  /**
   * Select the currently highlighted suggestion
   */
  selectCurrentSuggestion() {
    if (this.selectedSuggestionIndex >= 0) {
      const suggestions = this.suggestionsElement?.querySelectorAll('.flowlight-suggestion');
      if (suggestions && suggestions[this.selectedSuggestionIndex]) {
        const suggestion = suggestions[this.selectedSuggestionIndex].dataset.suggestion;
        this.selectSuggestion(suggestion);
      }
    }
  }

  /**
   * Navigate through search results with arrow keys
   */
  navigateResults(direction) {
    if (!this.currentResults || this.currentResults.length === 0) return;
    
    if (direction === 'down') {
      this.selectedResultIndex = Math.min(this.selectedResultIndex + 1, this.currentResults.length - 1);
    } else if (direction === 'up') {
      this.selectedResultIndex = Math.max(this.selectedResultIndex - 1, -1);
    }
    
    this.updateResultSelection();
  }

  /**
   * Update visual selection of results
   */
  updateResultSelection() {
    if (!this.resultsElement) return;
    
    const resultItems = this.resultsElement.querySelectorAll('.flowlight-flow-item');
    
    resultItems.forEach((item, index) => {
      if (index === this.selectedResultIndex) {
        // Selected state
        item.style.backgroundColor = '#e3f2fd';
        item.style.borderColor = '#2196f3';
        item.style.boxShadow = '0 2px 8px rgba(33, 150, 243, 0.3)';
      } else {
        // Default state
        item.style.backgroundColor = 'white';
        item.style.borderColor = '#dee2e6';
        item.style.boxShadow = 'none';
      }
    });
  }

  /**
   * Select the currently highlighted result
   */
  async selectCurrentResult() {
    if (this.selectedResultIndex >= 0 && this.selectedResultIndex < this.currentResults.length) {
      await this.selectFlow(this.currentResults[this.selectedResultIndex]);
    }
  }

  /**
   * Handle flow selection
   */
  async selectFlow(flow) {
    if (this.options.debug) {
      console.log('Selected flow:', flow);
      console.log('Flow object keys:', Object.keys(flow));
    }
    
    // Show loading state for flow details
    this.showLoadingState();
    
    try {
      // Call the flow details API
      const flowId = flow.id || flow.flow_id || flow._id || flow.flowId;
      
      if (!flowId) {
        throw new Error('Flow ID not found in flow object');
      }
      
      const flowDetails = await this.apiService.getFlowDetails(flowId);
      
      if (this.options.debug) {
        console.log('Flow details received:', flowDetails);
      }
      
      // Hide loading state
      this.hideLoadingState();
      
      // Hide search interface
      this.hide();
      
      // Emit flow selection event with flow details
      this.eventBus.emit('flow:selected', { flow, flowDetails });
      
      // Highlight elements from flow points
      if (flowDetails.success && flowDetails.points && flowDetails.points.length > 0) {
        this.setCookie(this.flowCurrentIdxCookieName, 0);
        this.setCookie(this.flowPointsCookieName, JSON.stringify(flowDetails.points));
        this.highlightFlowPoints();
      }
      
    } catch (error) {
      console.error('Error fetching flow details:', error);
      this.hideLoadingState();
      this.showErrorState('Failed to load flow details. Please try again.');
    }
  }

  /**
   * Set a cookie
   */
  setCookie(name, value, days = 7) {
    CookieService.setCookie(name, value, days);
  }

  /**
   * Get a cookie by name
   */
  getCookie(name) {
    return CookieService.getCookie(name);
  }

  /**
   * Highlight flow points one by one as the user interacts with the flow
   */
  highlightFlowPoints() {
    let points = JSON.parse(this.getCookie(this.flowPointsCookieName));
    let idx = parseInt(this.getCookie(this.flowCurrentIdxCookieName), 10);
    
    if (idx >= points.length) {
      console.log(MESSAGES.noMorePoints);
      return;
    }
    
    let currentPointLocator = points[idx].locator;
    let currentPointAction = points[idx].action || 'click';
    let currentPointSuggestionText = points[idx].description || '';

    console.log(MESSAGES.highlightingPoint, currentPointLocator, currentPointAction, currentPointSuggestionText);
    
    // Find the element using XPath
    let element = null;
    try {
      const xpathResult = document.evaluate(
        currentPointLocator,
        document,
        null,
        XPathResult.FIRST_ORDERED_NODE_TYPE,
        null
      );
      element = xpathResult.singleNodeValue;
    } catch (e) {
      console.error(MESSAGES.invalidXPath, currentPointLocator, e);
      element = null;
    }
    
    if (element) {
      // Configure highlight options based on action
      const highlightOptions = {
        dismissOnMouseEnter: currentPointAction !== 'read',
        autoDismissDelay: currentPointAction === 'read' ? 3500 : null
      };
      
      let overlay = this.highlightElement(element, highlightOptions);
      console.log(MESSAGES.overlayCreated, overlay);
      
      if (currentPointAction === 'read') {
        console.log(MESSAGES.readActionDetected);
      }
      
      // Add suggestion text on top of the highlighter
      if (currentPointSuggestionText && overlay) {
        const highlightBorder = overlay.querySelector('.flowlight-highlight-border');
        if (highlightBorder) {
          const suggestionElement = document.createElement('div');
          suggestionElement.className = 'flowlight-suggestion-text';
          
          // Style the suggestion text
          Object.assign(suggestionElement.style, {
            position: 'absolute',
            top: '-40px', // Position above the highlight border
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '14px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            fontWeight: '500',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            zIndex: '10',
            maxWidth: '300px',
            textAlign: 'center',
            wordWrap: 'break-word',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          });
          
          suggestionElement.textContent = currentPointSuggestionText;
          
          // Add the arrow using a pseudo-element alternative
          const arrow = document.createElement('div');
          Object.assign(arrow.style, {
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '0',
            height: '0',
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: '6px solid rgba(0, 0, 0, 0.9)'
          });
          suggestionElement.appendChild(arrow);
          
          highlightBorder.appendChild(suggestionElement);
        }
      }
      
      // Handle different action types
      if (currentPointAction === 'click') {
        // For click action, add click event listener
        element.addEventListener('click', () => {
          console.log(MESSAGES.elementClicked);
          callNextAPIHelper();
        });
        console.log(MESSAGES.clickActionDetected);
        
      } else if (currentPointAction === 'type') {
        // For type action, add input event listener with debounce
        let inputTimeout;
        
        const handleInput = () => {
          console.log(MESSAGES.inputDetected);
          
          // Clear existing timeout
          if (inputTimeout) {
            clearTimeout(inputTimeout);
          }
          
          // Set new timeout for 500ms idle
          inputTimeout = setTimeout(() => {
            console.log(MESSAGES.idleAfterInput);
            callNextAPIHelper();
          }, 500);
        };
        
        // Add input event listener
        element.addEventListener('input', handleInput);
        element.addEventListener('change', handleInput); // Also handle change events
        
        console.log(MESSAGES.typeActionDetected);
        
      } else {
        // For other actions (like 'read'), add default click behavior
        element.addEventListener('click', () => {
          console.log(MESSAGES.elementClicked);
          callNextAPIHelper();
        });
      }
      
      // Helper function to call next API
      const callNextAPIHelper = () => {
        idx = parseInt(this.getCookie(this.flowCurrentIdxCookieName), 10);
        this.setCookie(this.flowCurrentIdxCookieName, idx + 1);
        setTimeout(() => {
          this.highlightFlowPoints();
        }, 500);
      };
      
      return element;
    }
    return null;
  }

  /**
   * Highlight an element with overlay
   */
  highlightElement(element, options = {}) {
    if (!element) {
      console.error(MESSAGES.noElementProvided);
      return null;
    }

    // Remove any existing highlight
    this.removeHighlight();

    // Create highlight overlay
    const overlay = document.createElement('div');
    overlay.className = 'flowlight-highlight-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: ${this.options.zIndex - 2};
    `;

    // Create highlight border
    const border = document.createElement('div');
    border.className = 'flowlight-highlight-border';
    
    // Get element position and size
    const rect = element.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    
    border.style.cssText = `
      position: absolute;
      top: ${rect.top + scrollTop}px;
      left: ${rect.left + scrollLeft}px;
      width: ${rect.width}px;
      height: ${rect.height}px;
      border: 3px solid #007bff;
      border-radius: 4px;
      box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.3);
      pointer-events: none;
      z-index: ${this.options.zIndex - 1};
      animation: flowlight-pulse 2s infinite;
    `;

    // Add pulse animation styles
    if (!document.getElementById('flowlight-highlight-styles')) {
      const style = document.createElement('style');
      style.id = 'flowlight-highlight-styles';
      style.textContent = `
        @keyframes flowlight-pulse {
          0% { box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.3), 0 0 0 0 rgba(0, 123, 255, 0.7); }
          70% { box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.3), 0 0 0 10px rgba(0, 123, 255, 0); }
          100% { box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.3), 0 0 0 0 rgba(0, 123, 255, 0); }
        }
      `;
      document.head.appendChild(style);
    }

    overlay.appendChild(border);
    document.body.appendChild(overlay);

    // Handle auto-dismiss
    if (options.autoDismissDelay) {
      setTimeout(() => {
        this.removeHighlight();
      }, options.autoDismissDelay);
    }

    // Handle mouse enter dismissal
    if (options.dismissOnMouseEnter !== false) {
      element.addEventListener('mouseenter', () => {
        console.log(MESSAGES.dismissingHighlight);
        this.removeHighlight();
        console.log(MESSAGES.highlightDismissed);
      }, { once: true });
    }

    if (this.options.debug) {
      console.log(MESSAGES.elementHighlighted, element);
    }

    return overlay;
  }

  /**
   * Remove highlight overlay
   */
  removeHighlight() {
    const overlay = document.querySelector('.flowlight-highlight-overlay');
    if (overlay) {
      overlay.remove();
    }
  }

  /**
   * Destroy the search interface
   */
  destroy() {
    if (this.overlayElement && this.overlayElement.parentNode) {
      this.overlayElement.parentNode.removeChild(this.overlayElement);
    }
    if (this.searchElement && this.searchElement.parentNode) {
      this.searchElement.parentNode.removeChild(this.searchElement);
    }
    
    // Remove resize listener
    window.removeEventListener('resize', this.handleResize);
    
    // Remove highlight styles
    const highlightStyles = document.getElementById('flowlight-highlight-styles');
    if (highlightStyles) {
      highlightStyles.remove();
    }
    
    // Remove wave styles
    DomUtils.removeStyles('flowlight-wave-styles');
  }
} 